import { jsPDF } from 'jspdf';
import type { ModuleCatalogEntry } from '@/services/crm/moduleCatalogService';

export interface ModuleCatalogPdfGroup {
  systemName: string;
  items: ModuleCatalogEntry[];
}

export interface ModuleCatalogPdfLabels {
  title: string;
  purpose: string;
  functions: string;
  generated: string;
  pageOf: (page: number, pages: number) => string;
}

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 16;
const MARGIN_TOP = 16;
const MARGIN_BOTTOM = 16;
const FOOTER_Y = PAGE_H - 10;
const CONTENT_W = PAGE_W - MARGIN_X * 2;

const COLOR_TITLE: [number, number, number] = [17, 24, 39];
const COLOR_SYSTEM: [number, number, number] = [30, 64, 175];
const COLOR_MODULE: [number, number, number] = [17, 24, 39];
const COLOR_LABEL: [number, number, number] = [75, 85, 99];
const COLOR_BODY: [number, number, number] = [31, 41, 55];
const COLOR_MUTED: [number, number, number] = [107, 114, 128];
const COLOR_RULE: [number, number, number] = [191, 219, 254];

function lineHeightMm(fontSizePt: number): number {
  return fontSizePt * 0.4;
}

export function splitFunctionItems(text: string): string[] {
  const items = text
    .split(/\s*•\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  return items.length > 0 ? items : [text];
}

export function buildModuleCatalogPdf(
  groups: ModuleCatalogPdfGroup[],
  labels: ModuleCatalogPdfLabels
): jsPDF {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = MARGIN_TOP;

  const setType = (
    size: number,
    style: 'normal' | 'bold',
    color: [number, number, number]
  ) => {
    pdf.setFont('helvetica', style);
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
  };

  const ensureSpace = (neededMm: number) => {
    if (y + neededMm > PAGE_H - MARGIN_BOTTOM) {
      pdf.addPage();
      y = MARGIN_TOP;
    }
  };

  const writeLines = (
    text: string,
    size: number,
    style: 'normal' | 'bold',
    color: [number, number, number],
    indent = 0,
    extraGap = 0
  ) => {
    const maxWidth = CONTENT_W - indent;
    setType(size, style, color);
    const lines = pdf.splitTextToSize(text, maxWidth) as string[];
    const lh = lineHeightMm(size);
    for (const line of lines) {
      ensureSpace(lh);
      pdf.text(line, MARGIN_X + indent, y);
      y += lh;
    }
    y += extraGap;
  };

  setType(16, 'bold', COLOR_TITLE);
  pdf.text(labels.title, MARGIN_X, y);
  y += 6;
  setType(9, 'normal', COLOR_MUTED);
  pdf.text(labels.generated, MARGIN_X, y);
  y += 8;

  for (const group of groups) {
    ensureSpace(18);
    writeLines(group.systemName.toUpperCase(), 13, 'bold', COLOR_SYSTEM, 0, 1);
    pdf.setDrawColor(...COLOR_RULE);
    pdf.setLineWidth(0.4);
    pdf.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
    y += 5;

    for (const entry of group.items) {
      ensureSpace(16);
      writeLines(entry.name, 11, 'bold', COLOR_MODULE, 0, 1.2);

      if (entry.purpose?.trim()) {
        writeLines(labels.purpose, 8.5, 'bold', COLOR_LABEL, 2, 0.4);
        writeLines(entry.purpose.trim(), 9.5, 'normal', COLOR_BODY, 2, 2);
      }

      if (entry.functions?.trim()) {
        writeLines(labels.functions, 8.5, 'bold', COLOR_LABEL, 2, 0.6);
        const items = splitFunctionItems(entry.functions);
        if (items.length > 1) {
          for (const item of items) {
            writeLines(`•  ${item}`, 9.5, 'normal', COLOR_BODY, 2, 0.6);
          }
          y += 1.4;
        } else {
          writeLines(items[0], 9.5, 'normal', COLOR_BODY, 2, 2);
        }
      }

      y += 2.5;
    }

    y += 3;
  }

  const pages = pdf.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);
    setType(8, 'normal', COLOR_MUTED);
    pdf.text(labels.pageOf(page, pages), PAGE_W / 2, FOOTER_Y, { align: 'center' });
  }

  return pdf;
}

export function downloadModuleCatalogPdf(
  groups: ModuleCatalogPdfGroup[],
  labels: ModuleCatalogPdfLabels
): void {
  const pdf = buildModuleCatalogPdf(groups, labels);
  pdf.save(`modulkatalog-${new Date().toISOString().slice(0, 10)}.pdf`);
}
